import { useMemo } from 'react';
import { Specs } from '@doxyme/extensions-bridge-react';
import { useLocale } from '@doxyme/locale';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import {
  getUsername,
  getRole,
  getUserState,
  getUserPicture,
} from '../../../features/realtime/realtime.selectors';
import { useAppSelector } from '../../useReduxToolkit';
import { usePersonalRoom } from '../../usePersonalRoom';
import { useThemeNameByRoute } from '../../useThemeNameByRoute';
import { useUserSubscription } from '../../useUserSubscription';
import { useGetUserId } from '../../../hooks/useCredentials';
import { useUserEntitlementsContext } from '../../../context';
import { FronteggFeatureKeys } from '../../../context/userEntitlementsContext.types';
import { Permission, useUserPermissions } from '../../useAuth';
import { usePatientWaitingRoom } from '../../../views/waiting-room/waitingRoomView/hooks/usePatientWaitingRoom/usePatientWaitingRoom';
import { ProviderFeatureKey } from '../../../services/graphQlTypes.generated';

const { FronteggExtensionsFeatureKeys, FronteggExtensionsPermissionKeys } =
  Specs.ReceiveContext;

type Role = Specs.ReceiveContext.Role;

type EntitlementCheck = {
  isEntitled: boolean;
  appFeature: Specs.ReceiveContext.FronteggExtensionsFeatureKeys;
};

function featuresFromEntitlements(
  entitlementChecks: EntitlementCheck[],
): Specs.ReceiveContext.FronteggExtensionsFeatureKeys[] {
  return entitlementChecks.filter((c) => c.isEntitled).map((c) => c.appFeature);
}

function filterPermissions(
  permissions: Permission[],
): Specs.ReceiveContext.FronteggExtensionsPermissionKeys[] {
  return Object.values(FronteggExtensionsPermissionKeys).filter((p) => {
    return permissions.some((permission) => permission.key === p);
  });
}

function useExtensionContextFeaturesAndPermissions(): {
  features: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
  permissions: Specs.ReceiveContext.FronteggExtensionsPermissionKeys[];
} {
  const { useFeatureEntitlements } = useUserEntitlementsContext();
  const pro = useFeatureEntitlements(FronteggFeatureKeys.ProPlan);
  const premium = useFeatureEntitlements(FronteggFeatureKeys.PremiumPlan);
  const { permissions } = useUserPermissions();

  const entitlementChecks: EntitlementCheck[] = useMemo(
    () => [
      {
        isEntitled: pro.isEntitled,
        appFeature: FronteggExtensionsFeatureKeys.ProPlan,
      },
      {
        isEntitled: premium.isEntitled,
        appFeature: FronteggExtensionsFeatureKeys.PremiumPlan,
      },
    ],
    [pro.isEntitled, premium.isEntitled],
  );

  return useMemo(
    () => ({
      features: featuresFromEntitlements(entitlementChecks),
      permissions: filterPermissions(permissions),
    }),
    [entitlementChecks, permissions],
  );
}

export type UseExtensionsReceiveContextBaseReturnType = {
  role?: Role;
  username?: string;
  avatar?: string;
  clinicId?: string;
  locale?: string;
  theme?: string;
  sessionId?: string;
};

export type UseExtensionsReceiveContextProviderReturnType = {
  personalRoomUrl?: string;
  subscriptionType?: string;
  userId?: string;
  features?: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
  permissions?: Specs.ReceiveContext.FronteggExtensionsPermissionKeys[];
};

export type UseExtensionsReceiveContextPatientReturnType = {
  ip?: string;
  features?: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
};

/**
 * Hook responsible for providing context to the Extensions SDK, will be more data later
 */
export function useExtensionsReceiveContextBase(): UseExtensionsReceiveContextBaseReturnType {
  const { institutionId: clinicId, sessionId } = useAppSelector(
    getApolloSessionDetails,
  );
  const username = useAppSelector(getUsername);
  const role = useAppSelector(getRole);
  const avatar = useAppSelector(getUserPicture);
  const { locale } = useLocale();
  const theme = useThemeNameByRoute();

  const context = useMemo(() => {
    return {
      username,
      avatar,
      clinicId,
      role: role as Role | undefined,
      locale,
      theme,
      sessionId: Boolean(sessionId) ? sessionId : undefined,
    };
  }, [username, avatar, clinicId, role, locale, theme, sessionId]);

  return context;
}

/**
 * Hook responsible for providing provider context to the Extensions SDK, will be more data later
 */
export function useExtensionsReceiveContextProvider(): UseExtensionsReceiveContextProviderReturnType {
  const { roomUrl: personalRoomUrl } = usePersonalRoom();
  const { plan } = useUserSubscription();
  const userId = useGetUserId();
  const { features, permissions } = useExtensionContextFeaturesAndPermissions();

  return useMemo(
    () => ({
      personalRoomUrl,
      subscriptionType: plan,
      userId: userId || undefined,
      features,
      permissions,
    }),
    [personalRoomUrl, plan, userId, features, permissions],
  );
}

/**
 * Hook responsible for providing patient context to the Extensions SDK, will be more data later
 */
export function useExtensionsReceiveContextPatient(): UseExtensionsReceiveContextPatientReturnType {
  const ip = useAppSelector(getUserState)?.privateData?.ip;
  const { room } = usePatientWaitingRoom();

  const context = useMemo(() => {
    const features = mapRoomFeaturesToContextFeatures(
      room?.providerEntitlements?.features,
    );

    return {
      ip,
      features,
    };
  }, [ip, room?.providerEntitlements?.features]);

  return context;
}

const ROOM_FEATURE_TO_CONTEXT_FEATURE: Partial<
  Record<ProviderFeatureKey, Specs.ReceiveContext.FronteggExtensionsFeatureKeys>
> = {
  [ProviderFeatureKey.ProPlan]: FronteggExtensionsFeatureKeys.ProPlan,
  [ProviderFeatureKey.Premium]: FronteggExtensionsFeatureKeys.PremiumPlan,
};

const mapRoomFeaturesToContextFeatures = (
  features?: ProviderFeatureKey[],
): Specs.ReceiveContext.FronteggExtensionsFeatureKeys[] | undefined => {
  if (!features || features?.length === 0) {
    return undefined;
  }

  return features
    .map((feature) => ROOM_FEATURE_TO_CONTEXT_FEATURE[feature])
    .filter(Boolean) as Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
};
